import {useState, useCallback, useImperativeHandle, useMemo, forwardRef} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition, BulkUpdateDefinitionItem} from '@/types/attribute-definition'
import {ParameterEditor} from './ParameterEditor'
import {useServices} from '@/services/context'

export interface ParameterDefinitionEditorHandle {
    saveAll: () => Promise<void>
}

interface ParameterDefinitionEditorProps {
    reportDefinitionId: string
}

export const ParameterDefinitionEditor = forwardRef<ParameterDefinitionEditorHandle, ParameterDefinitionEditorProps>(
    function ParameterDefinitionEditor({reportDefinitionId}, ref) {
        const services = useServices()
        const queryClient = useQueryClient()

        // eslint-disable-next-line i18next/no-literal-string -- query key
        const queryKey = useMemo(() => ['reporting-attribute-definitions', reportDefinitionId], [reportDefinitionId])

        const {data: serverDefinitions = []} = useQuery<AttributeDefinition[]>({
            queryKey,
            queryFn: () => services.reporting.reportAttributeDefinitions(reportDefinitionId) as Promise<AttributeDefinition[]>,
        })

        // Local edits buffered until explicit save
        const [localEdits, setLocalEdits] = useState<Record<string, Record<string, unknown>>>({})

        const definitions = serverDefinitions.map(def => {
            const edits = localEdits[def.id]
            return edits ? {...def, ...edits} as AttributeDefinition : def
        })

        const createMutation = useEntityMutation({
            mutationFn: (body: Record<string, unknown>) =>
                services.reporting.createReportAttributeDefinition(reportDefinitionId, body),
            invalidate: queryKey,
        })

        const deleteMutation = useEntityMutation({
            mutationFn: (definitionId: string) =>
                services.reporting.removeReportAttributeDefinition(reportDefinitionId, definitionId),
            invalidate: queryKey,
            onSuccess: (_data, deletedId) => {
                setLocalEdits(prev => {
                    const next = {...prev}
                    delete next[deletedId]
                    return next
                })
            },
        })

        const handleUpdate = useCallback((def: AttributeDefinition, body: Record<string, unknown>) => {
            setLocalEdits(prev => ({...prev, [def.id]: {...(prev[def.id] ?? {}), ...body}}))
        }, [])

        // Bulk save all pending edits in a single request
        useImperativeHandle(ref, () => ({
            async saveAll() {
                const editsToSave = Object.entries(localEdits)
                if (editsToSave.length === 0) return

                const items: BulkUpdateDefinitionItem[] = editsToSave.map(([id, body]) => {
                    const def = serverDefinitions.find(d => d.id === id)
                    if (!def) throw new Error(`No server definition for id ${id}`)
                    return {...def, ...body, id, version: def.version} as BulkUpdateDefinitionItem
                })

                await services.reporting.updateReportAttributeDefinitions(reportDefinitionId, items)
                setLocalEdits({})
                queryClient.invalidateQueries({queryKey})
            }
        }), [localEdits, serverDefinitions, reportDefinitionId, queryClient, queryKey, services.reporting])

        return (
            <ParameterEditor
                definitions={definitions}
                onAdd={name => createMutation.mutate({
                    name,
                    displayName: name,
                    valueType: AttributeValueType.STRING,
                    operations: [],
                    required: false,
                    sortOrder: definitions.length,
                    active: true,
                    searchable: true,
                    filterable: true,
                })}
                onUpdate={handleUpdate}
                onDelete={id => deleteMutation.mutate(id)}
                isAdding={createMutation.isPending}
            />
        )
    }
)
